import { useEffect, useMemo, useState } from 'react';
import { Typography, Grid } from '@mui/material';
import { useQueryClient } from 'react-query';

// Our components
import Alert from 'components/Alert';
import { PrimaryButton } from 'components/Button/Button';
import CurrencyTextInput from 'components/Input/CurrencyTextInput';
import Dropdown from 'components/Dropdown/Dropdown';
import GetFullAddressPartialForm from 'components/Forms/addressRelated/GetFullAddressPartialForm';
import Loader from 'components/Loader';
import LoanInquirySuccessModal from 'components/Modal/LoanInquiryConfirmationModal';
import OutstandingBalanceInput from 'components/Client/Onboarding/OutstandingBalanceInput';
import SoraTextField from 'components/Input/SoraTextField';
import { NUMBER } from 'components/Input/Types';

// Our Query Keys
import { ERROR_MESSAGE_DATA } from 'shared/query-keys';

// Our hooks 🪝
import useMutateCreateDeal from 'hooks/hubspot/useMutateCreateDeal';
import useMutateSaveNewLoanInquiry from 'hooks/newLoanInquiries/useMutateSaveNewLoanInquiry';
import useMutateUpdateLoanInquiry from 'hooks/newLoanInquiries/useMutateUpdatedLoanInquiry';
import useMutateSendGenericEmail from 'hooks/emails/useMutateSendGenericEmail';

// Utils
import {
	clearFormValues,
	dollarFormatter,
	isSubmissionReady
} from 'shared/utils';
import createDefaultCloseDate from 'shared/utils/hubspot/createDefaultCloseDate';
import formatCollection from 'shared/utils/formatting/currency/formatCollection';

// Our emails
import lendingEmailTemplate from 'emails/lendingEmailTemplate';

// Constants
import {
	EMPLOYMENT_STATUS_OPTIONS,
	REFINANCE_OFFER_TYPE,
	REVERSEMORTGAGE
} from 'shared/constants';

function ReverseMortgageForm({
	advisorInfo,
	clientInfo,
	isClientNameProvided,
	loanType,
	loanRequestId,
	financialData,
	clientMortgageInfo,
	postSuccessEvent,
	disableAutoComplete,
	setDisableAutoComplete
}) {
	const queryClient = useQueryClient();
	const updateLoanInquiry = useMutateUpdateLoanInquiry();

	// API Calls
	const saveLoanInquiry = useMutateSaveNewLoanInquiry();
	const { isLoading: savingLoanInquiry } = saveLoanInquiry;

	// Mutations
	const sendGenericEmail = useMutateSendGenericEmail();
	const createDeal = useMutateCreateDeal();

	// Build full name
	const {
		clientFullName,
		clientId,
		firstName,
		lastName,
		annualIncome,
		creditScoreValue
	} = clientInfo;

	const { advisorName, advisorEmail, advisorCompany, advisorWebsite } =
		advisorInfo;

	const { currentOutstandingBalance, refinancingTradelineId } = financialData;

	const {
		addressLine1Value,
		addressLine2Value,
		cityValue,
		estimatedValue,
		primaryUseValue,
		stateValue,
		zipCodeValue
	} = clientMortgageInfo;

	// Pre-populated state items
	const [loanAmount, setLoanAmount] = useState('');
	const [employmentStatus, setEmploymentStatus] = useState('Employed');

	// Address related fields
	const [addressLine1, setAddressLine1] = useState('');
	const [addressLine2, setAddressLine2] = useState('');
	const [city, setCity] = useState('');
	const [state, setState] = useState('');
	const [zipCode, setZipCode] = useState('');

	const [ownershipType, setOwnershipType] = useState('');
	const [estimatedHomeValue, setEstimatedHomeValue] = useState('');
	const [outstandingBalance, setOutstandingBalance] = useState('');

	const [additionalNotes, setAdditionalNotes] = useState('');
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

	const requiredSubmissionFields = useMemo(
		() =>
			isSubmissionReady([addressLine1, city, state, zipCode, loanAmount]),
		[addressLine1, city, state, zipCode, loanAmount]
	);
	const isFormReady = useMemo(
		() => isClientNameProvided && requiredSubmissionFields,
		[isClientNameProvided, requiredSubmissionFields]
	);

	useEffect(() => {
		if (currentOutstandingBalance) {
			setOutstandingBalance(currentOutstandingBalance);
		}
		if (estimatedValue) {
			setEstimatedHomeValue(estimatedValue);
		}
		if (primaryUseValue) {
			if (primaryUseValue === 'PRIMARY') {
				setOwnershipType('Primary Residence');
			}
		}
		if (addressLine1Value) {
			setAddressLine1(addressLine1Value);
		}
		if (addressLine2Value) {
			setAddressLine2(addressLine2Value);
		}
		if (cityValue) {
			setCity(cityValue);
		}
		if (stateValue) {
			setState(`${stateValue}`);
		}
		if (zipCodeValue) {
			setZipCode(zipCodeValue);
		}
	}, [
		currentOutstandingBalance,
		estimatedValue,
		primaryUseValue,
		addressLine1Value,
		addressLine2Value,
		cityValue,
		stateValue,
		zipCodeValue
	]);

	const sendFormData = () => {
		const sendToArray = ['lending@sorafinance.com'];
		const subject = `Reverse Mortgage Loan Request - ${firstName} ${lastName}`;

		const emailTemplate = lendingEmailTemplate({
			advisorInfo,
			clientInfo,
			financialData,
			clientFullName,
			loanAmount,
			loanRelatedDetails: [
				`Client's Employment Status: ${employmentStatus}`,
				`Client's Estimated Home Value: ${dollarFormatter.format(
					+estimatedHomeValue
				)}`,
				`Client's Outstanding Mortgage Balance: ${dollarFormatter.format(
					+outstandingBalance
				)}`,
				`Client's expected ownership type: ${ownershipType}`,
				`Property address: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}`
			],
			additionalNotes,
			lendingTypeRequested: 'Reverse Mortgage Loan'
		});

		sendGenericEmail.mutate(
			{
				firstName,
				lastName,
				subject,
				message: emailTemplate,
				sendToArray
			},
			{
				onSettled: () => {
					// Open Success modal
					setIsSuccessModalOpen(true);
				}
			}
		);
	};

	const submitNewLoanInquiry = () => {
		const newLoanInquiryData = {
			additionalNotes,
			addressLine1,
			addressLine2,
			city,
			clientId,
			creditScoreValue,
			employmentStatus,
			estimatedHomeValue,
			firstName,
			lastName,
			offerType: REFINANCE_OFFER_TYPE,
			outstandingBalance,
			ownershipType,
			loanAmount,
			state,
			totalAnnualIncome: annualIncome,
			tradelineType: REVERSEMORTGAGE,
			tradelineId: refinancingTradelineId,
			zipCode
		};

		// Call mutation to update user's data
		saveLoanInquiry.mutate(newLoanInquiryData, {
			onSuccess: () => {
				setIsSuccessModalOpen(true);
			},
			onError: () => {
				// Set error message
				queryClient.setQueryData(
					ERROR_MESSAGE_DATA,
					'There was an error making your loan request.'
				);
			}
		});
	};

	const updateLoanRequest = () => {
		const updatedLoanInquiryData = {
			additionalNotes,
			addressLine1,
			addressLine2,
			city,
			clientId,
			creditScore: creditScoreValue,
			employmentStatus,
			estimatedHomeValue,
			firstName,
			lastName,
			offerType: REFINANCE_OFFER_TYPE,
			outstandingBalance,
			ownershipType,
			loanAmount,
			state,
			totalAnnualIncome: annualIncome,
			tradelineType: REVERSEMORTGAGE,
			zipCode
		};

		// Call mutation to update user's data
		updateLoanInquiry.mutate(
			{ loanRequestId, updatedLoanInquiryData },
			{
				onSuccess: () => {
					clearFormValues([
						setAddressLine1,
						setAddressLine2,
						setCity,
						setState,
						setZipCode,
						setLoanAmount,
						setOwnershipType,
						setEstimatedHomeValue,
						setOutstandingBalance,
						setAdditionalNotes,
						setEmploymentStatus
					]);

					if (postSuccessEvent) {
						postSuccessEvent();
					}
				},
				onError: () => {
					// Set error message
					queryClient.setQueryData(
						ERROR_MESSAGE_DATA,
						'There was an error making your loan request.'
					);
				}
			}
		);
	};

	const { isLoading } = sendFormData;

	const currentURL = window.location.href;
	const atLoanRequestDetailsUrl = currentURL.includes('loan-request-details');

	const submitForm = (event) => {
		event.preventDefault();

		const closeDate = createDefaultCloseDate();
		const valuesToFormat = [
			{
				type: 'currency',
				value: annualIncome,
				label: 'Annual Income:'
			},
			{
				type: 'currency',
				value: loanAmount,
				label: 'Desired Reverse Mortgage Amount:'
			},
			{
				type: 'currency',
				value: estimatedHomeValue,
				label: 'Estimated Home Value:'
			},
			{
				type: 'currency',
				value: outstandingBalance,
				label: 'Current Mortgage Outstanding Balance:'
			}
		];

		const formattedCollection = formatCollection(valuesToFormat);
		const advisorNotes = `Advisor's name: ${advisorName}
				Advisor's email: ${advisorEmail}
				Advisor's company: ${advisorCompany}
				Credit Score: ${creditScoreValue}
				${formattedCollection}
				Primary Use: ${ownershipType}
				Employment Status: ${employmentStatus}
				Address: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}
				------------------------------------------------------------------------------------------------
				Advisor's notes: \n${additionalNotes}`;

		createDeal.mutate({
			advisorInfo: {
				advisorName,
				email: advisorEmail,
				companyName: advisorCompany,
				companyDomain: advisorWebsite
			},
			properties: {
				amount: loanAmount,
				dealname: `${advisorName} / ${clientFullName}`,
				dealtype: loanType,
				borrower: clientFullName,
				closedate: closeDate
			},
			noteInfo: {
				hs_note_body: advisorNotes
			}
		});

		// If we're at /loan-inquiry, perform the following
		if (!atLoanRequestDetailsUrl) {
			submitNewLoanInquiry();
			sendFormData();
		}
		// Else if we're at /loan-inquiry-details, perform the following
		else if (atLoanRequestDetailsUrl) {
			// Call new mutations here patching the new loan inquiry
			updateLoanRequest();
		}
	};

	// Handlers for mutation
	const { isError: sendFormDataError } = sendFormData;

	return (
		<>
			{!atLoanRequestDetailsUrl && (
				<Grid item xs={12}>
					<Typography variant="h2Gascogne">
						Reverse Mortgage Loan
					</Typography>

					<Typography variant="body2">
						Fill in the information below to submit a loan request
						to Sora. You will be able to complete and review your
						loan request in the dashboard task bar.
					</Typography>
				</Grid>
			)}

			{isLoading || (savingLoanInquiry && <Loader />)}
			{!isLoading && !savingLoanInquiry && (
				<>
					{sendFormDataError && <Alert variant="error" />}

					{/* Modal shown on successful submission of  */}
					<LoanInquirySuccessModal isOpen={isSuccessModalOpen} />

					<Grid item xs={12} md={6}>
						{/* Desired Amount */}
						<CurrencyTextInput
							label="Desired Reverse Mortgage Amount"
							value={loanAmount}
							onChange={setLoanAmount}
							required
							inputProps={{
								'data-test': 'desiredLoanAmount'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Estimated Home Value */}
						<CurrencyTextInput
							label="Estimated Home Value"
							subLabel="Estimated price of home"
							value={estimatedHomeValue}
							onChange={setEstimatedHomeValue}
							inputProps={{
								'data-test': 'estimatedHomeValue'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Outstanding Balance */}
						<OutstandingBalanceInput
							label="Outstanding Balance of Current Loan"
							outstandingBalance={outstandingBalance}
							setOutstandingBalance={setOutstandingBalance}
							inputProps={{
								'data-test': 'outstandingBalance'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Ownership type */}
						<Dropdown
							items={[
								'Primary Residence',
								'Secondary Residence',
								'Investment Property'
							]}
							selected={ownershipType}
							onChange={setOwnershipType}
							variant="outlined"
							label="Primary Use of Property"
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Employment Status */}
						<Dropdown
							items={EMPLOYMENT_STATUS_OPTIONS}
							selected={employmentStatus}
							onChange={setEmploymentStatus}
							variant="outlined"
							label="Employment status"
						/>
					</Grid>

					{/* Address Rows */}
					<GetFullAddressPartialForm
						addressLine1={addressLine1}
						addressLine2={addressLine2}
						city={city}
						state={state}
						zipCode={zipCode}
						setAddressLine1={setAddressLine1}
						setAddressLine2={setAddressLine2}
						setCity={setCity}
						setState={setState}
						setZipCode={setZipCode}
						disableAutoComplete={disableAutoComplete}
						setDisableAutoComplete={setDisableAutoComplete}
						isRequired
					/>

					<Grid item xs={12}>
						<SoraTextField
							label="Additional Notes"
							value={additionalNotes}
							onChange={setAdditionalNotes}
						/>
					</Grid>
				</>
			)}

			<Grid item xs={12}>
				<PrimaryButton disabled={!isFormReady} onClick={submitForm}>
					Submit
				</PrimaryButton>
			</Grid>
		</>
	);
}

export default ReverseMortgageForm;
